'use client';

import { Button, Stack } from '@mui/material';
import {
  Configuration,
  DocumentReaderApi,
  GraphicFieldType,
  Scenario,
} from '@regulaforensics/document-reader-webclient';

// We cannot use these directly, due to it produces ` HTMLElement is not defined` errors,
// the potential reason is we are using `pages router` which not compatible `use client` totally,
// migrated to `app router` may fixed the issue.
// Thus use `any` and hard code `scenario` to make sure it can be build successfully
// import {
//   DocumentReaderDetailType,
//   DocumentReaderResponseType,
//   DocumentReaderWebComponent,
//   DocumentReaderService,
//   IDocumentReader,
// } from '@regulaforensics/vp-frontend-document-components';

import { DetailedHTMLProps, HTMLAttributes, useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    // @ts-ignore
    RegulaDocumentSDK: any;
  }

  namespace JSX {
    interface IntrinsicElements {
      'document-reader': DetailedHTMLProps<HTMLAttributes<any>, any>;
    }
  }
}

const configuration = new Configuration({ basePath: 'https://nightly-api.regulaforensics.com' });
const api = new DocumentReaderApi(configuration);

export default function DocumentReader({
  license,
  onSuccess,
}: Readonly<{
  license?: string;
  onSuccess?: (response: any) => void;
}>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInitializationPerformed = useRef(false);
  const elementRef = useRef<any>(null);

  const listener = async (data: CustomEvent<any>) => {
    if (data.detail.action === 'PROCESS_FINISHED') {
      const status = data.detail.data?.status;
      // Success
      if (status === 1) {
        const componentResponse = data.detail.data.response;

        onSuccess?.(componentResponse);

        const imageField = componentResponse.images.getField(GraphicFieldType.DOCUMENT_FRONT);
        const documentFront = imageField.valueList[1].value;
        const processParam = {
          images: [documentFront],
          processParam: {
            scenario: Scenario.FULL_PROCESS,
          },
        };

        const result = await api.process(processParam);

        setTimeout(() => setIsOpen(false), 1000);
      }
    }

    if (data.detail?.action === 'CLOSE') {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isInitializationPerformed.current) {
      return;
    }

    const initDocumentReaderService = async () => {
      isInitializationPerformed.current = true;
      const { defineComponents, DocumentReaderService } = await import(
        '@regulaforensics/vp-frontend-document-components'
      );

      window.RegulaDocumentSDK = new DocumentReaderService();
      window.RegulaDocumentSDK.recognizerProcessParam = {
        processParam: {
          returnUncroppedImage: true,
          // @ts-ignore
          scenario: 'MrzOrLocate',
          multipageProcessing: false,
          returnPackageForReprocess: false,
          timeout: 20000,
          resultTypeOutput: [],
          imageQa: {
            expectedPass: ['dpiThreshold', 'glaresCheck', 'focusCheck'],
            dpiThreshold: 300,
            glaresCheck: true,
            glaresCheckParams: {
              imgMarginPart: 0.05,
              maxGlaringPart: 0.01,
            },
          },
        },
      };

      // To use the document-reader component on test environments, you have to set the base64 license
      if (license) {
        defineComponents().then(() => window.RegulaDocumentSDK.initialize({ license }));
      } else {
        // Production environment
        defineComponents().then(() => window.RegulaDocumentSDK.initialize());
      }
    };

    initDocumentReaderService();

    const containerCurrent = containerRef.current;

    if (!containerCurrent) {
      return;
    }

    containerCurrent.addEventListener('document-reader', listener);

    return () => {
      if (!window.RegulaDocumentSDK) {
        return;
      }

      window.RegulaDocumentSDK.shutdown();

      containerCurrent.removeEventListener('document-reader', listener);
    };
  }, []);

  useEffect(() => {
    const elementRefCurrent = elementRef.current;

    if (!elementRefCurrent) {
      return;
    }

    elementRefCurrent.settings = {
      startScreen: true,
      changeCameraButton: true,
    };
  }, [isOpen]);

  return (
    <Stack ref={containerRef}>
      {isOpen ? (
        <document-reader ref={elementRef}></document-reader>
      ) : (
        <Button variant="contained" onClick={() => setIsOpen(true)}>
          Open Document Reader
        </Button>
      )}
    </Stack>
  );
}
